import React from "react";
import _ from 'lodash'

import { CompetitorFamily, CompetitorMember, CompetitorOriginationLink, CompetitorAgentLink, CompetitorCommodityLink } from "./external";
import { useCrud, useLinkedCrud } from "../BackendProvider";
import { competitor_family, competitor_member, competitor_origination_link, competitor_agent_link, competitor_commodity_link } from "../data";
import { ExternalContext } from ".";


export default function ExternalProvider({ children }: { children: React.ReactNode }) {
    const { data: families, postMutation: postFamily, deleteMutation: deleteFamily, isLoading: isLoadingFamilies, reload: reloadFamilies } =
        useCrud<CompetitorFamily>(competitor_family, f => f.competitorFamilyId)

    const { data: members, postMutation: postMember, deleteMutation: deleteMember, isLoading: isLoadingMembers, reload: reloadMembers } =
        useCrud<CompetitorMember>(competitor_member, m => m.competitorMemberId)

    const { data: originationLinks, postMutation: postOriginationLink, deleteMutation: deleteOriginationLink } =
        useLinkedCrud<CompetitorOriginationLink>(
            competitor_origination_link,
            (a, b) => a.from === b.from && a.to === b.to
        )

    const { data: competitorAgentLinks, postMutation: postCompetitorAgentLink, deleteMutation: deleteCompetitorAgentLink } =
        useLinkedCrud<CompetitorAgentLink>(
            competitor_agent_link,
            (a, b) => a.competitorMemberId === b.competitorMemberId && a.agentId === b.agentId && a.linkType === b.linkType
        )

    const { data: competitorCommodityLinks, postMutation: postCompetitorCommodityLink, deleteMutation: deleteCompetitorCommodityLink } =
        useLinkedCrud<CompetitorCommodityLink>(
            competitor_commodity_link,
            (a, b) => a.competitorFamilyId === b.competitorFamilyId && a.commodityId === b.commodityId
        )

    const familyById = _.keyBy(families, f => f.competitorFamilyId)
    const familyByReference = _.keyBy(families, f => f.internalReference)

    const memberById = _.keyBy(members, m => m.competitorMemberId)
    const memberByReference = _.keyBy(members, m => m.internalReference)
    const membersByFamilyId = _.groupBy(members, m => m.competitorFamilyId)

    // parents: memberId -> [parentMemberIds]
    const parents = _(originationLinks).groupBy('from').mapValues(ls => ls.map(l => l.to)).value()

    // linkType -> memberId -> [agentIds]
    const agentsByMemberId: Record<string, Record<number, number[]>> = _(competitorAgentLinks)
        .groupBy(l => l.linkType)
        .mapValues(ls => _(ls)
            .groupBy(l => l.competitorMemberId)
            .mapValues(vs => vs.map(v => v.agentId))
            .value())
        .value()

    const isLoading = isLoadingFamilies || isLoadingMembers

    function reload() {
        reloadFamilies()
        reloadMembers()
    }

    const value = {
        families,
        familyById,
        familyByReference,
        postFamily,
        deleteFamily,

        members,
        memberById,
        memberByReference,
        membersByFamilyId,
        postMember,
        deleteMember,

        originationLinks,
        postOriginationLink,
        deleteOriginationLink,

        competitorAgentLinks,
        postCompetitorAgentLink,
        deleteCompetitorAgentLink,
        agentsByMemberId,

        competitorCommodityLinks,
        postCompetitorCommodityLink,
        deleteCompetitorCommodityLink,

        parents,

        isLoading,
        reload,
    }

    return <ExternalContext.Provider {...{value}} >
        {children}
    </ExternalContext.Provider>;
}
